import React, { useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

// Shared full-screen image viewer — originally LandingPage.jsx's own
// ProductTourLightbox (built for the product-tour screenshot gallery),
// extracted here so HelpPage.jsx's tutorial images can reuse the exact same
// click-to-enlarge behavior instead of a second, duplicate implementation.
// Takes primitives (src/alt/title/desc) rather than a caller-specific "shot"
// object, so either page can supply whatever metadata it actually has —
// LandingPage.jsx's PRODUCT_TOUR_SHOTS entries all have a title/desc,
// HelpPage.jsx's tutorial images don't and just omit those props.
export default function ImageLightbox({ src, alt, title, desc, onClose, onPrev, onNext }) {
  const isPdf = typeof src === 'string' && src.toLowerCase().endsWith('.pdf');

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && onPrev) onPrev();
      else if (e.key === 'ArrowRight' && onNext) onNext();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, onPrev, onNext]);

  return (
    <div
      className="fixed inset-0 z-[250] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 sm:p-8"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        className="image-lightbox-icon-btn absolute top-4 right-4 sm:top-6 sm:right-6 p-2 rounded-lg hover:bg-slate-800 transition z-10"
      >
        <X className="w-6 h-6" />
      </button>

      {onPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          aria-label="Previous screenshot"
          className="image-lightbox-icon-btn flex absolute left-1.5 sm:left-6 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 rounded-lg bg-black/40 sm:bg-transparent hover:bg-slate-800 transition z-10"
        >
          <ChevronLeft className="w-6 h-6 sm:w-7 sm:h-7" />
        </button>
      )}
      {onNext && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          aria-label="Next screenshot"
          className="image-lightbox-icon-btn flex absolute right-1.5 sm:right-6 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 rounded-lg bg-black/40 sm:bg-transparent hover:bg-slate-800 transition z-10"
        >
          <ChevronRight className="w-6 h-6 sm:w-7 sm:h-7" />
        </button>
      )}

      <div className="max-w-4xl w-full max-h-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        {isPdf ? (
          <>
            <iframe
              src={src}
              title={alt || title || 'Document preview'}
              className="w-full max-w-3xl h-[75vh] rounded-lg bg-white shadow-2xl"
            />
            {/* Inline PDF rendering isn't reliable on every mobile browser
                (iOS Safari in particular), so this is a guaranteed fallback
                rather than the primary way to view the document. */}
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              className="image-lightbox-desc text-sm mt-3 underline hover:no-underline"
            >
              Open in a new tab
            </a>
          </>
        ) : (
          <img
            src={src}
            alt={alt || ''}
            className="max-w-full max-h-[75vh] w-auto h-auto object-contain"
          />
        )}
        {title && <h3 className="image-lightbox-title text-lg sm:text-xl font-bold mt-5 text-center">{title}</h3>}
        {desc && <p className="image-lightbox-desc text-sm mt-1.5 max-w-lg text-center">{desc}</p>}
      </div>
    </div>
  );
}
